import React, { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import { useCart } from '../contexts/CartContext'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabaseClient'
import MediaWithShimmer from '../components/MediaWithShimmer'
import ImageGalleryModal from '../components/ImageGalleryModal'

const ProductDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { language } = useLanguage()
  const { cart, addToCart, updateQuantity } = useCart()
  const { canViewHidden, loading: authLoading } = useAuth()

  const [product, setProduct] = useState(null)
  const [loadingProduct, setLoadingProduct] = useState(true)
  const [relatedProducts, setRelatedProducts] = useState([])
  const [relatedImageIndex, setRelatedImageIndex] = useState({})
  const [selectedImage, setSelectedImage] = useState(0)
  const [selectedSize, setSelectedSize] = useState('')
  const [selectedColor, setSelectedColor] = useState('')
  const [galleryModal, setGalleryModal] = useState(false)
  
  // Comments state
  const [comments, setComments] = useState([])
  const [newComment, setNewComment] = useState('')
  const [loadingComments, setLoadingComments] = useState(false)
  const [submittingComment, setSubmittingComment] = useState(false)
  

  useEffect(() => {
    const load = async () => {
      // Wait for auth to finish loading before checking permissions
      if (authLoading) {
        return
      }
      
      setLoadingProduct(true)
      // Fetch product by id
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', Number(id))
        .maybeSingle()
      if (error || !data) {
        setProduct(null)
        setLoadingProduct(false)
        return
      }
      const p = {
        ...data,
        nameEn: data.nameEn ?? data.nameen ?? '',
        nameEs: data.nameEs ?? data.namees ?? '',
        descriptionEn: data.descriptionEn ?? data.descriptionen ?? '',
        descriptionEs: data.descriptionEs ?? data.descriptiones ?? '',
        images: Array.isArray(data.images) && data.images.length > 0 ? data.images : (data.image ? [data.image] : []),
        sizes: Array.isArray(data.sizes) ? data.sizes : [],
        colors: Array.isArray(data.colors) ? data.colors : [],
        stock: typeof data.stock === 'number' ? data.stock : 0,
      }
      
      // Check if product is hidden and user doesn't have permission
      if (p.is_hidden && !canViewHidden) {
        navigate('/')
        return
      }
      
      setProduct(p)

      // Related products by category
      if (p.category) {
        let query = supabase
          .from('products')
          .select('*')
          .eq('category', p.category)
          .neq('id', p.id)
        
        // Filter out hidden products if user doesn't have permission
        if (!canViewHidden) {
          query = query.or('is_hidden.is.null,is_hidden.eq.false')
        }
        
        const { data: rel } = await query
          .order('id', { ascending: true })
          .limit(4)
        
        const normalized = (rel || []).map(r => ({
          ...r,
          nameEn: r.nameEn ?? r.nameen ?? '',
          nameEs: r.nameEs ?? r.namees ?? '',
        }))
        setRelatedProducts(normalized)
      }
      setLoadingProduct(false)
    }
    load()
  }, [id, canViewHidden, navigate, authLoading])

  // Load comments
  useEffect(() => {
    const loadComments = async () => {
      if (!id) return
      
      setLoadingComments(true)
      const { data, error } = await supabase
        .from('product_comments')
        .select(`
          *,
          profiles:user_id (
            full_name,
            email
          )
        `)
        .eq('product_id', Number(id))
        .order('created_at', { ascending: false })
      
      if (!error && data) {
        setComments(data)
      }
      setLoadingComments(false)
    }
    
    loadComments()
  }, [id])

  // Submit comment
  const handleSubmitComment = async (e) => {
    e.preventDefault()
    if (!newComment.trim()) return
    
    setSubmittingComment(true)
    
    // Try to get user, but allow anonymous comments
    const { data: { user } } = await supabase.auth.getUser()
    
    const { data, error } = await supabase
      .from('product_comments')
      .insert([
        {
          product_id: Number(id),
          user_id: user?.id || null, // Allow null for anonymous users
          comment: newComment.trim()
        }
      ])
      .select(`
        *,
        profiles:user_id (
          full_name,
          email
        )
      `)
    
    if (!error && data) {
      setComments([data[0], ...comments])
      setNewComment('')
    } else {
      alert(language === 'ru' ? 'Ошибка отправки комментария' : 'Error posting comment')
    }
    setSubmittingComment(false)
  }

  if (authLoading || loadingProduct) {
    return (
      <div className="min-h-screen flex items-center justify-center ">
        <div className="text-center">
          <div className="w-10 h-10 rounded-full border-2 border-black border-t-transparent animate-spin mx-auto mb-4"></div>
        </div>
      </div>
    )
  }
  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center ">
        <div className="text-center">
          <h1 className="text-4xl font-light mb-4 playfair">
            {language === 'ru' ? 'Товар не найден' : language === 'en' ? 'Product not found' : 'Producto no encontrado'}
          </h1>
          <Link to="/shop" className="text-black underline montserrat">
            {language === 'ru' ? 'Вернуться в магазин' : language === 'en' ? 'Back to shop' : 'Volver a la tienda'}
          </Link>
        </div>
      </div>
    )
  }

  const getProductName = () => {
    return language === 'en' ? product.nameEn : language === 'es' ? product.nameEs : product.name
  }

  // Fix dash rendering in Bugaki font
  const renderProductName = () => {
    const name = getProductName()
    if (!name) return ''
    
    // Replace hyphens/dashes with a span that uses a different font
    const parts = name.split(/(-|–|—)/)
    return parts.map((part, index) => {
      if (part === '-' || part === '–' || part === '—') {
        return <span key={index} style={{ fontFamily: 'Georgia, serif' }}> - </span>
      }
      return part
    })
  }

  const getProductDescription = () => {
    return language === 'en' ? product.descriptionEn : language === 'es' ? product.descriptionEs : product.description
  }

  // Get material with proper language support
  const getMaterial = () => {
    if (!product.material) return null
    
    // If English translation exists in database, use it
    if (language === 'en' && product.materialen) {
      return product.materialen
    }
    
    // Otherwise use machine translation
    return translateMaterial(product.material)
  }

  // Simple machine translation for material field (fallback)
  const translateMaterial = (material) => {
    if (!material) return material
    
    const translations = {
      // Common materials
      'экокожа': { en: 'eco-leather', es: 'cuero ecológico' },
      'кожа': { en: 'leather', es: 'cuero' },
      'шелк': { en: 'silk', es: 'seda' },
      'хлопок': { en: 'cotton', es: 'algodón' },
      'шерсть': { en: 'wool', es: 'lana' },
      'кашемир': { en: 'cashmere', es: 'cachemira' },
      'лен': { en: 'linen', es: 'lino' },
      'полиэстер': { en: 'polyester', es: 'poliéster' },
      'нейлон': { en: 'nylon', es: 'nailon' },
      'замша': { en: 'suede', es: 'ante' },
      'атлас': { en: 'satin', es: 'satén' },
      'бархат': { en: 'velvet', es: 'terciopelo' },
      'джинса': { en: 'denim', es: 'mezclilla' },
      'трикотаж': { en: 'knitwear', es: 'punto' },
      'вискоза': { en: 'viscose', es: 'viscosa' },
      'эластан': { en: 'elastane', es: 'elastano' },
      'спандекс': { en: 'spandex', es: 'spandex' },
      'акрил': { en: 'acrylic', es: 'acrílico' },
      'мех': { en: 'fur', es: 'piel' },
      'синтетика': { en: 'synthetic', es: 'sintético' },
      'органза': { en: 'organza', es: 'organza' },
      'тюль': { en: 'tulle', es: 'tul' },
      'кружево': { en: 'lace', es: 'encaje' },
      'фатин': { en: 'tulle', es: 'tul' },
      'парча': { en: 'brocade', es: 'brocado' },
      'жаккард': { en: 'jacquard', es: 'jacquard' },
      'шифон': { en: 'chiffon', es: 'gasa' },
      'креп': { en: 'crepe', es: 'crepé' },
      'габардин': { en: 'gabardine', es: 'gabardina' },
      'твид': { en: 'tweed', es: 'tweed' },
      'флис': { en: 'fleece', es: 'polar' },
      'микрофибра': { en: 'microfiber', es: 'microfibra' },
      'перья страуса': { en: 'ostrich feathers', es: 'plumas de avestruz' },
      'перья': { en: 'feathers', es: 'plumas' },
      'страуса': { en: 'ostrich', es: 'avestruz' }
    }
    
    if (language === 'ru') return material
    
    let translated = material.toLowerCase()
    
    // Sort by length (longest first) to handle multi-word phrases first
    const sortedKeys = Object.keys(translations).sort((a, b) => b.length - a.length)
    
    // Replace each Russian word/phrase with translation using word boundaries
    sortedKeys.forEach(ru => {
      const regex = new RegExp(`\\b${ru}\\b`, 'gi')
      translated = translated.replace(regex, translations[ru][language])
    })
    
    // Capitalize first letter
    return translated.charAt(0).toUpperCase() + translated.slice(1)
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ru-RU').format(price).replace(/\s/g, '\u00A0') + '\u00A0€'
  }

  // Build dynamic details list without hardcoding values; only show existing fields
  const getDetailEntries = () => {
    const entries = []
    const p = product || {}
    const pushIf = (label, value) => {
      if (value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0)) {
        entries.push([label, Array.isArray(value) ? value.join(', ') : String(value)])
      }
    }
    pushIf(language === 'ru' ? 'Артикул' : language === 'en' ? 'SKU' : 'SKU', p.sku || p.code || p.id)
    pushIf(language === 'ru' ? 'Категория' : language === 'en' ? 'Category' : 'Categoría', p.category)
    pushIf(language === 'ru' ? 'Бренд' : language === 'en' ? 'Brand' : 'Marca', p.brand)
    pushIf(language === 'ru' ? 'Материал' : language === 'en' ? 'Material' : 'Material', getMaterial())
    pushIf(language === 'ru' ? 'Объем' : language === 'en' ? 'Volume' : 'Volumen', p.volume)
    pushIf(language === 'ru' ? 'Вес' : language === 'en' ? 'Weight' : 'Peso', p.weight)
    pushIf(language === 'ru' ? 'Размеры' : language === 'en' ? 'Dimensions' : 'Dimensiones', p.dimensions)
    pushIf(language === 'ru' ? 'Страна' : language === 'en' ? 'Country' : 'País', p.country || p.origin)
    pushIf(language === 'ru' ? 'Цвета' : language === 'en' ? 'Colors' : 'Colores', p.colors)
    pushIf(language === 'ru' ? 'Размеры' : language === 'en' ? 'Sizes' : 'Tallas', p.sizes)
    // Fragrance-specific optional fields
    pushIf(language === 'ru' ? 'Верхние ноты' : language === 'en' ? 'Top notes' : 'Notas de salida', p.notesTop)
    pushIf(language === 'ru' ? 'Сердечные ноты' : language === 'en' ? 'Heart notes' : 'Notas de corazón', p.notesHeart)
    pushIf(language === 'ru' ? 'Базовые ноты' : language === 'en' ? 'Base notes' : 'Notas de fondo', p.notesBase)
    pushIf(language === 'ru' ? 'Пол' : language === 'en' ? 'Gender' : 'Género', p.gender)
    return entries
  }

  const handleAddToCart = () => {
    if (!selectedSize && product.sizes.length > 0) {
      alert(language === 'ru' ? 'Выберите размер' : language === 'en' ? 'Select size' : 'Seleccione talla')
      return
    }
    if (!selectedColor && product.colors.length > 0) {
      alert(language === 'ru' ? 'Выберите цвет' : language === 'en' ? 'Select color' : 'Seleccione color')
      return
    }
    if ((product.stock ?? 0) <= 0) {
      alert(language === 'ru' ? 'Нет в наличии' : language === 'en' ? 'Out of stock' : 'Agotado')
      return
    }

    addToCart(product, selectedSize, selectedColor)
  }

  const translations = {
    backToShop: { ru: 'Назад в магазин', en: 'Back to Shop', es: 'Volver a la Tienda' },
    selectSize: { ru: 'Выберите размер', en: 'Select Size', es: 'Seleccione Talla' },
    selectColor: { ru: 'Выберите цвет', en: 'Select Color', es: 'Seleccione Color' },
    quantity: { ru: 'Количество', en: 'Quantity', es: 'Cantidad' },
    addToCart: { ru: 'Добавить в корзину', en: 'Add to Cart', es: 'Añadir al Carrito' },
    buyNow: { ru: 'Купить сейчас', en: 'Buy Now', es: 'Comprar Ahora' },
    description: { ru: 'Описание', en: 'Description', es: 'Descripción' },
    details: { ru: 'Детали', en: 'Details', es: 'Detalles' },
    inStock: { ru: 'В наличии', en: 'In Stock', es: 'En Stock' },
    outOfStock: { ru: 'Нет в наличии', en: 'Out of Stock', es: 'Agotado' },
    addedToCart: { ru: 'Товар добавлен в корзину!', en: 'Product added to cart!', es: '¡Producto añadido al carrito!' }
  }

  const t = translations

  const related = relatedProducts

  // Current cart item for selected variant
  const currentItem = cart.find(
    (it) => it.id === product.id && it.size === (selectedSize || '') && it.color === (selectedColor || '')
  )
  const currentQty = currentItem?.quantity || 0

  return (
    <div className="bg-white pt-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6 pb-6">
        {/* Back to Shop Link */}
        <div className="mb-3">
          <Link to="/shop" className="text-gray-600 hover:text-black montserrat text-sm">
            <i className="ri-arrow-left-line mr-2"></i>
            {t.backToShop[language]}
          </Link>
        </div>

        {/* Mobile Layout: Info → Images → Actions */}
        <div className="lg:hidden">
          {/* Product Name Above Images */}
          <div className="mb-4">
            <h1 className="text-3xl md:text-4xl bugaki capitalize text-center font-black" style={{ color: '#FF1493' }}>{renderProductName()}</h1>
          </div>

          {/* Images */}
          <div className="mb-8">
            <div 
              className="mb-3 aspect-[3/4] sm:aspect-[2/3] overflow-hidden bg-gray-100 w-full max-w-[480px] rounded-md mx-auto cursor-pointer relative"
              onClick={() => setGalleryModal(true)}
              onTouchStart={(e) => {
                if (product.images && product.images.length > 1) {
                  const touch = e.touches[0]
                  e.currentTarget.dataset.touchStartX = touch.clientX
                  e.currentTarget.dataset.touchStartY = touch.clientY
                  e.currentTarget.dataset.swiped = 'false'
                }
              }}
              onTouchMove={(e) => {
                if (product.images && product.images.length > 1) {
                  const touch = e.touches[0]
                  const startX = parseFloat(e.currentTarget.dataset.touchStartX || '0')
                  const startY = parseFloat(e.currentTarget.dataset.touchStartY || '0')
                  const currentX = touch.clientX
                  const currentY = touch.clientY
                  const diffX = Math.abs(startX - currentX)
                  const diffY = Math.abs(startY - currentY)
                  const swiped = e.currentTarget.dataset.swiped === 'true'
                  
                  // Prevent page scroll if horizontal movement is detected
                  if (diffX > 10 && diffX > diffY) {
                    e.preventDefault()
                  }
                  
                  if (diffX > diffY && diffX > 80 && !swiped) {
                    e.currentTarget.dataset.swiped = 'true'
                    
                    if (startX - currentX > 0) {
                      const newIndex = Math.min(selectedImage + 1, product.images.length - 1)
                      if (newIndex !== selectedImage) {
                        setSelectedImage(newIndex)
                      }
                    } else {
                      const newIndex = Math.max(selectedImage - 1, 0)
                      if (newIndex !== selectedImage) {
                        setSelectedImage(newIndex)
                      }
                    }
                  }
                }
              }}
              onTouchEnd={(e) => {
                if (product.images && product.images.length > 1) {
                  e.currentTarget.dataset.touchStartX = '0'
                  e.currentTarget.dataset.touchStartY = '0'
                  e.currentTarget.dataset.swiped = 'false'
                }
              }}
            >
              {/* Image container with all images */}
              <div 
                className="flex w-full h-full transition-transform duration-300 ease-out"
                style={{ 
                  transform: `translateX(-${selectedImage * 100}%)`
                }}
              >
                {product.images && product.images.map((img, idx) => (
                  <div key={idx} className="w-full h-full flex-shrink-0">
                    <MediaWithShimmer
                      src={img}
                      alt={`${getProductName()} - ${idx + 1}`}
                      className="w-full h-full object-contain"
                      eager={idx === 0}
                    />
                  </div>
                ))}
              </div>
              
              {/* Dots indicator */}
              {product.images && product.images.length > 1 && (
                <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex gap-2 pointer-events-none">
                  {product.images.map((_, idx) => (
                    <div
                      key={idx}
                      className={`rounded-full transition-all duration-300 ${
                        idx === selectedImage 
                          ? 'bg-black w-2 h-2' 
                          : 'bg-gray-400 w-1.5 h-1.5'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
            {product.images && product.images.length > 1 && (
              <div className="hidden sm:grid grid-cols-5 gap-2 max-w-[480px]">
                {product.images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`aspect-square overflow-hidden border-2 rounded ${
                      selectedImage === index ? 'border-black' : 'border-gray-300'
                    }`}
                  >
                    <MediaWithShimmer src={img} alt="" className="w-full h-full object-contain" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Size and Color Selection after Images */}
          <div className="space-y-4 mb-3">
            {/* Size Selection */}
            {Array.isArray(product.sizes) && product.sizes.length > 0 && (
              <div>
                <label className="block text-sm font-medium mb-3 montserrat">{t.selectSize[language]}</label>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`px-4 py-2 border transition-colors montserrat text-sm ${
                        selectedSize === size
                          ? 'bg-black text-white border-black'
                          : 'border-gray-300 hover:border-black'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Color Selection */}
            {Array.isArray(product.colors) && product.colors.length > 0 && (
              <div>
                <label className="block text-sm font-medium mb-3 montserrat">{t.selectColor[language]}</label>
                <div className="flex flex-wrap gap-2">
                  {product.colors.map((color) => (
                    <button
                      key={color}
                      onClick={() => setSelectedColor(color)}
                      className={`px-4 py-2 border transition-colors montserrat text-sm ${
                        selectedColor === color
                          ? 'bg-black text-white border-black'
                          : 'border-gray-300 hover:border-black'
                      }`}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Actions and Accordions */}
          <div className="space-y-4">
            {/* Action Area */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {currentQty === 0 ? (
                <button
                  onClick={handleAddToCart}
                  disabled={(product.stock ?? 0) <= 0}
                  className="w-full bg-black text-white py-3 hover:bg-gray-800 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed montserrat"
                >
                  {t.addToCart[language]}
                </button>
              ) : (
                <div className="flex items-center justify-between md:justify-start md:gap-4">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => updateQuantity(product.id, selectedSize || '', selectedColor || '', currentQty - 1)}
                      className="w-9 h-9 border border-gray-300 hover:border-black transition-colors"
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <span className="text-base md:text-lg montserrat w-12 text-center">{currentQty}</span>
                    <button
                      onClick={() => updateQuantity(product.id, selectedSize || '', selectedColor || '', currentQty + 1)}
                      className="w-9 h-9 border border-gray-300 hover:border-black transition-colors"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>
                </div>
              )}

              <button
                onClick={() => {
                  if (currentQty === 0) {
                    if (!selectedSize && product.sizes.length > 0) {
                      alert(language === 'ru' ? 'Выберите размер' : language === 'en' ? 'Select size' : 'Seleccione talla')
                      return
                    }
                    if (!selectedColor && product.colors.length > 0) {
                      alert(language === 'ru' ? 'Выберите цвет' : language === 'en' ? 'Select color' : 'Seleccione color')
                      return
                    }
                    if ((product.stock ?? 0) <= 0) {
                      alert(language === 'ru' ? 'Нет в наличии' : language === 'en' ? 'Out of stock' : 'Agotado')
                      return
                    }
                    handleAddToCart()
                  }
                  navigate('/cart')
                }}
                disabled={(product.stock ?? 0) <= 0}
                className="w-full border-2 border-black text-black py-3 hover:bg-black hover:text-white transition-colors disabled:border-gray-300 disabled:text-gray-300 disabled:cursor-not-allowed montserrat"
              >
                {t.buyNow[language]}
              </button>
            </div>

            {/* Description */}
            <div className="mt-6">
              <h3 className="text-lg font-medium montserrat mb-3">Description</h3>
              <p className="text-gray-700 text-sm leading-relaxed montserrat whitespace-pre-line">{getProductDescription()}</p>
            </div>
          </div>
        </div>

        {/* Desktop Layout: Images left, Info right */}
        <div className="hidden lg:grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Images - слева на десктопе */}
          <div className="lg:pr-8">
            {/* Product Name Above Images */}
            <div className="mb-4">
              <h1 className="text-3xl md:text-4xl bugaki capitalize text-center font-black" style={{ color: '#FF1493' }}>{renderProductName()}</h1>
            </div>
            <div 
              className="mb-3 aspect-[3/4] sm:aspect-[2/3] lg:aspect-[3/4] overflow-hidden bg-gray-100 w-full max-w-[600px] lg:max-h-[85vh] rounded-md mx-auto cursor-pointer relative"
              onClick={() => setGalleryModal(true)}
              onTouchStart={(e) => {
                if (product.images && product.images.length > 1) {
                  const touch = e.touches[0]
                  e.currentTarget.dataset.touchStartX = touch.clientX
                  e.currentTarget.dataset.touchStartY = touch.clientY
                  e.currentTarget.dataset.swiped = 'false'
                }
              }}
              onTouchMove={(e) => {
                if (product.images && product.images.length > 1) {
                  const touch = e.touches[0]
                  const startX = parseFloat(e.currentTarget.dataset.touchStartX || '0')
                  const startY = parseFloat(e.currentTarget.dataset.touchStartY || '0')
                  const currentX = touch.clientX
                  const currentY = touch.clientY
                  const diffX = Math.abs(startX - currentX)
                  const diffY = Math.abs(startY - currentY)
                  const swiped = e.currentTarget.dataset.swiped === 'true'
                  
                  if (diffX > 10 && diffX > diffY) {
                    e.preventDefault()
                  }
                  
                  if (diffX > diffY && diffX > 80 && !swiped) {
                    e.currentTarget.dataset.swiped = 'true'
                    
                    if (startX - currentX > 0) {
                      const newIndex = Math.min(selectedImage + 1, product.images.length - 1)
                      if (newIndex !== selectedImage) {
                        setSelectedImage(newIndex)
                      }
                    } else {
                      const newIndex = Math.max(selectedImage - 1, 0)
                      if (newIndex !== selectedImage) {
                        setSelectedImage(newIndex)
                      }
                    }
                  }
                }
              }}
              onTouchEnd={(e) => {
                if (product.images && product.images.length > 1) {
                  e.currentTarget.dataset.touchStartX = '0'
                  e.currentTarget.dataset.touchStartY = '0'
                  e.currentTarget.dataset.swiped = 'false'
                }
              }}
            >
              <div 
                className="flex w-full h-full transition-transform duration-300 ease-out"
                style={{ 
                  transform: `translateX(-${selectedImage * 100}%)`
                }}
              >
                {product.images && product.images.map((img, idx) => (
                  <div key={idx} className="w-full h-full flex-shrink-0">
                    <MediaWithShimmer
                      src={img}
                      alt={`${getProductName()} - ${idx + 1}`}
                      className="w-full h-full object-contain"
                      eager={idx === 0}
                    />
                  </div>
                ))}
              </div>
              
              {product.images && product.images.length > 1 && (
                <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex gap-2 pointer-events-none">
                  {product.images.map((_, idx) => (
                    <div
                      key={idx}
                      className={`rounded-full transition-all duration-300 ${
                        idx === selectedImage 
                          ? 'bg-black w-2 h-2' 
                          : 'bg-gray-400 w-1.5 h-1.5'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
            {product.images && product.images.length > 1 && (
              <div className="hidden sm:grid grid-cols-5 gap-2 max-w-[480px]">
                {product.images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`aspect-square overflow-hidden border-2 rounded ${
                      selectedImage === index ? 'border-black' : 'border-gray-300'
                    }`}
                  >
                    <MediaWithShimmer src={img} alt="" className="w-full h-full object-contain" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info - справа на десктопе */}
          <div className="space-y-4">
            {/* Size Selection */}
            {Array.isArray(product.sizes) && product.sizes.length > 0 && (
              <div>
                <label className="block text-sm font-medium mb-3 montserrat">{t.selectSize[language]}</label>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`px-4 py-2 border transition-colors montserrat text-sm ${
                        selectedSize === size
                          ? 'bg-black text-white border-black'
                          : 'border-gray-300 hover:border-black'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Color Selection */}
            {Array.isArray(product.colors) && product.colors.length > 0 && (
              <div>
                <label className="block text-sm font-medium mb-3 montserrat">{t.selectColor[language]}</label>
                <div className="flex flex-wrap gap-2">
                  {product.colors.map((color) => (
                    <button
                      key={color}
                      onClick={() => setSelectedColor(color)}
                      className={`px-4 py-2 border transition-colors montserrat text-sm ${
                        selectedColor === color
                          ? 'bg-black text-white border-black'
                          : 'border-gray-300 hover:border-black'
                      }`}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Action Area */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {currentQty === 0 ? (
                <button
                  onClick={handleAddToCart}
                  disabled={(product.stock ?? 0) <= 0}
                  className="w-full bg-black text-white py-3 hover:bg-gray-800 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed montserrat"
                >
                  {t.addToCart[language]}
                </button>
              ) : (
                <div className="flex items-center justify-between md:justify-start md:gap-4">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => updateQuantity(product.id, selectedSize || '', selectedColor || '', currentQty - 1)}
                      className="w-9 h-9 border border-gray-300 hover:border-black transition-colors"
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <span className="text-base md:text-lg montserrat w-12 text-center">{currentQty}</span>
                    <button
                      onClick={() => updateQuantity(product.id, selectedSize || '', selectedColor || '', currentQty + 1)}
                      className="w-9 h-9 border border-gray-300 hover:border-black transition-colors"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>
                </div>
              )}

              <button
                onClick={() => {
                  if (currentQty === 0) {
                    // Check if size/color are required and selected
                    if (!selectedSize && product.sizes.length > 0) {
                      alert(language === 'ru' ? 'Выберите размер' : language === 'en' ? 'Select size' : 'Seleccione talla')
                      return
                    }
                    if (!selectedColor && product.colors.length > 0) {
                      alert(language === 'ru' ? 'Выберите цвет' : language === 'en' ? 'Select color' : 'Seleccione color')
                      return
                    }
                    if ((product.stock ?? 0) <= 0) {
                      alert(language === 'ru' ? 'Нет в наличии' : language === 'en' ? 'Out of stock' : 'Agotado')
                      return
                    }
                    handleAddToCart()
                  }
                  navigate('/cart')
                }}
                disabled={(product.stock ?? 0) <= 0}
                className="w-full border-2 border-black text-black py-3 hover:bg-black hover:text-white transition-colors disabled:border-gray-300 disabled:text-gray-300 disabled:cursor-not-allowed montserrat"
              >
                {t.buyNow[language]}
              </button>
            </div>

            {/* Description */}
            <div className="mt-6">
              <h3 className="text-lg font-medium montserrat mb-3">Description</h3>
              <p className="text-gray-700 text-sm leading-relaxed montserrat whitespace-pre-line">{getProductDescription()}</p>
            </div>
          </div>
        </div>

        {/* Comments Section */}
        <div className="mt-6">
          <h2 className="text-xl font-medium playfair mb-4">
            {language === 'ru' ? 'Комментарии' : language === 'en' ? 'Comments' : 'Comentarios'}
          </h2>
          
          {/* Comments List */}
          {loadingComments ? (
            <div className="text-center py-4 mb-6">
              <div className="w-6 h-6 rounded-full border-2 border-black border-t-transparent animate-spin mx-auto"></div>
            </div>
          ) : comments.length > 0 ? (
            <div className="space-y-4 mb-6">
              {comments.map((comment) => (
                <div key={comment.id} className="bg-gray-50 p-4 rounded-md">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-user-line text-gray-600 text-sm"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900 montserrat text-sm">
                          {comment.profiles?.full_name || comment.profiles?.email || (language === 'ru' ? 'Аноним' : language === 'en' ? 'Anonymous' : 'Anónimo')}
                        </span>
                        <span className="text-xs text-gray-500 montserrat">
                          {new Date(comment.created_at).toLocaleDateString(language === 'ru' ? 'ru-RU' : language === 'es' ? 'es-ES' : 'en-US')}
                        </span>
                      </div>
                      <p className="text-gray-700 montserrat text-sm leading-relaxed">
                        {comment.comment}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4 montserrat text-sm mb-6">
              {language === 'ru' ? 'Пока нет комментариев' : language === 'en' ? 'No comments yet' : 'Aún no hay comentarios'}
            </p>
          )}

          {/* Comment Form */}
          <form onSubmit={handleSubmitComment}>
            <div className="mb-3">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={language === 'ru' ? 'Напишите комментарий...' : language === 'en' ? 'Write a comment...' : 'Escribe un comentario...'}
                className="w-full p-3 border border-gray-300 rounded-md resize-none focus:outline-none focus:border-black montserrat text-sm"
                rows="3"
                disabled={submittingComment}
              />
            </div>
            <button
              type="submit"
              disabled={!newComment.trim() || submittingComment}
              className="px-4 py-2 bg-black text-white text-xs tracking-wide uppercase font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed montserrat"
            >
              {submittingComment 
                ? (language === 'ru' ? 'Отправка...' : language === 'en' ? 'Posting...' : 'Enviando...')
                : (language === 'ru' ? 'Отправить' : language === 'en' ? 'Post Comment' : 'Enviar')
              }
            </button>
          </form>
        </div>

        {/* Related Products */}
        {related.length > 0 && (
          <div className="mt-3 pt-3">
            <h2 className="text-3xl font-light playfair mb-3">
              {language === 'ru' ? 'Похожие товары' : language === 'en' ? 'Related Products' : 'Productos Relacionados'}
            </h2>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-6 sm:gap-3 lg:gap-4">
              {related.map((relatedProduct) => {
                const relatedImages = Array.isArray(relatedProduct.images) && relatedProduct.images.length > 0 
                  ? relatedProduct.images 
                  : (relatedProduct.image ? [relatedProduct.image] : [])
                
                const currentIndex = relatedImageIndex[relatedProduct.id] || 0
                
                return (
                  <div
                    key={relatedProduct.id}
                    className="group bg-white w-full sm:w-[calc(50%-0.375rem)] lg:w-[calc(25%-0.75rem)] sm:flex-shrink-0"
                  >
                    <Link
                      to={`/shop/${relatedProduct.id}`}
                      className="block relative overflow-hidden aspect-[3/4] mb-3"
                      onTouchStart={(e) => {
                        if (relatedImages.length <= 1) return
                        const touch = e.touches[0]
                        e.currentTarget.dataset.touchStartX = touch.clientX
                        e.currentTarget.dataset.touchStartY = touch.clientY
                        e.currentTarget.dataset.swiped = 'false'
                      }}
                      onTouchMove={(e) => {
                        if (relatedImages.length <= 1) return
                        const touch = e.touches[0]
                        const startX = parseFloat(e.currentTarget.dataset.touchStartX || '0')
                        const startY = parseFloat(e.currentTarget.dataset.touchStartY || '0')
                        const currentX = touch.clientX
                        const currentY = touch.clientY
                        const diffX = Math.abs(startX - currentX)
                        const diffY = Math.abs(startY - currentY)
                        const swiped = e.currentTarget.dataset.swiped === 'true'
                        
                        // Prevent page scroll if horizontal movement is detected
                        if (diffX > 10 && diffX > diffY) {
                          e.preventDefault()
                        }
                        
                        if (diffX > diffY && diffX > 80 && !swiped) {
                          e.currentTarget.dataset.swiped = 'true'
                          
                          if (startX - currentX > 0) {
                            const newIndex = Math.min(currentIndex + 1, relatedImages.length - 1)
                            if (newIndex !== currentIndex) {
                              setRelatedImageIndex(prev => ({ ...prev, [relatedProduct.id]: newIndex }))
                            }
                          } else {
                            const newIndex = Math.max(currentIndex - 1, 0)
                            if (newIndex !== currentIndex) {
                              setRelatedImageIndex(prev => ({ ...prev, [relatedProduct.id]: newIndex }))
                            }
                          }
                        }
                      }}
                      onTouchEnd={(e) => {
                        if (relatedImages.length <= 1) return
                        e.currentTarget.dataset.touchStartX = '0'
                        e.currentTarget.dataset.touchStartY = '0'
                        e.currentTarget.dataset.swiped = 'false'
                      }}
                    >
                      {/* Image container with all images */}
                      <div 
                        className="flex w-full h-full transition-transform duration-300 ease-out"
                        style={{ 
                          transform: `translateX(-${currentIndex * 100}%)`
                        }}
                      >
                        {relatedImages.map((img, idx) => (
                          <div key={idx} className="w-full h-full flex-shrink-0">
                            <MediaWithShimmer
                              src={img}
                              alt={`${language === 'en' ? relatedProduct.nameEn : language === 'es' ? relatedProduct.nameEs : relatedProduct.name} - ${idx + 1}`}
                              className="w-full h-full object-contain"
                              eager={idx === 0}
                            />
                          </div>
                        ))}
                      </div>
                      
                      {/* Badge */}
                      {relatedProduct.badge && (
                        <div className="absolute top-3 left-3 pointer-events-none">
                          <span className="bg-white text-gray-800 px-3 py-1 text-[10px] uppercase tracking-wider montserrat font-normal">
                            {relatedProduct.badge}
                          </span>
                        </div>
                      )}
                      
                      {/* Wishlist icon */}
                      <button 
                        className="absolute top-3 right-3 w-10 h-10 flex items-center justify-center hover:opacity-70 transition-opacity z-10"
                        onClick={(e) => {
                          e.preventDefault()
                        }}
                      >
                        <i className="ri-heart-line text-2xl text-gray-700"></i>
                      </button>
                      
                      {/* Dots indicator */}
                      {relatedImages.length > 1 && (
                        <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex gap-2 pointer-events-none">
                          {relatedImages.map((_, idx) => (
                            <div 
                              key={idx} 
                              className={`rounded-full transition-all duration-300 ${
                                idx === currentIndex 
                                  ? 'bg-black w-2 h-2' 
                                  : 'bg-gray-400 w-1.5 h-1.5'
                              }`}
                            />
                          ))}
                        </div>
                      )}
                    </Link>
                    
                    {/* Product info */}
                    <div className="flex items-start justify-between gap-2">
                      <Link to={`/shop/${relatedProduct.id}`} className="flex-1">
                        <h3 className="text-sm sm:text-base font-medium mb-1 montserrat leading-tight">
                          {language === 'en' ? relatedProduct.nameEn : language === 'es' ? relatedProduct.nameEs : relatedProduct.name}
                        </h3>
                        <p className="text-sm sm:text-base font-normal montserrat text-gray-900">
                          {formatPrice(relatedProduct.price)}
                        </p>
                      </Link>
                      <button 
                        className="flex-shrink-0 w-8 h-8 flex items-center justify-center hover:opacity-70 transition-opacity"
                        onClick={(e) => {
                          e.preventDefault()
                          addToCart(relatedProduct, '', '')
                        }}
                      >
                        <i className="ri-shopping-bag-line text-xl text-gray-700"></i>
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>

      {/* Image Gallery Modal */}
      {galleryModal && product.images && product.images.length > 0 && (
        <ImageGalleryModal
          images={product.images}
          initialIndex={selectedImage}
          onClose={() => setGalleryModal(false)}
        />
      )}
    </div>
  )
}

export default ProductDetail

